import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { CatalogOffering, CatalogService } from '../catalog/catalog.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreateInsuranceProductPayload,
  InsurancePolicyDocumentTemplate,
  InsuranceApiService,
  InsuranceProductDetail,
  InsuranceProductVersionListItem,
  UpdateInsuranceProductPayload
} from './insurance-api.service';

type ProductTab = 'summary' | 'versions';

@Component({
  standalone: true,
  selector: 'ain-insurance-product-detail',
  imports: [
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    RouterLink,
    FormsModule,
    InputTextModule,
    SelectModule,
    CheckboxModule,
    PartySelectorComponent,
    JalaliDateInputComponent,
    ButtonModule,
    TableModule
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('insurance.productDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.products.help.page.title'"
        [descriptionKey]="'insurance.products.help.page.description'"
        [bulletKeys]="[
          'insurance.products.help.page.bullets.scope',
          'insurance.products.help.page.bullets.versions',
          'insurance.products.help.page.bullets.offering'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (isCreateMode()) {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/products']">{{ t('catalog.action.backToList') }}</a>
          <button
            pButton
            type="button"
            [label]="t('common.save')"
            [disabled]="saving() || !createCanSave()"
            (click)="saveCreate()"></button>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.products.sections.info') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.products.help.info.title'"
              [descriptionKey]="'insurance.products.help.info.description'"
              [bulletKeys]="[
                'insurance.products.help.info.bullets.lob',
                'insurance.products.help.info.bullets.dates',
                'insurance.products.help.info.bullets.offering'
              ]" />
          </div>
          <form class="grid" (ngSubmit)="saveCreate()">
            <label>
              <span>{{ t('insurance.products.fields.legalEntityId') }}</span>
              <ain-party-selector [(ngModel)]="createForm.legalEntityId" name="legalEntityId" />
            </label>
            <label>
              <span>{{ t('insurance.products.fields.code') }}</span>
              <input pInputText [(ngModel)]="createForm.code" name="code" />
            </label>
            <label>
              <span>{{ t('insurance.products.fields.name') }}</span>
              <input pInputText [(ngModel)]="createForm.name" name="name" />
            </label>
            <label>
              <span>{{ t('insurance.products.fields.offeringId') }}</span>
              <p-select
                [options]="offeringOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="createForm.offeringId"
                (ngModelChange)="onCreateOfferingChanged($event)"
                name="offeringId"
                [showClear]="true"
                [filter]="true"
                appendTo="body" />
            </label>
            <label>
              <span>{{ t('insurance.products.fields.policyDocumentTemplateId') }}</span>
              <p-select
                [options]="templateOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="createForm.policyDocumentTemplateId"
                name="policyDocumentTemplateId"
                [showClear]="true"
                [filter]="true"
                appendTo="body" />
            </label>
            <label>
              <span>{{ t('insurance.products.fields.lineOfBusiness') }}</span>
              <p-select
                [options]="lineOfBusinessOptions()"
                optionValue="value"
                [(ngModel)]="createForm.lineOfBusiness"
                name="lineOfBusiness"
                [showClear]="true"
                [filter]="true"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? item.label : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ item.label }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.products.fields.active') }}</span>
              <p-checkbox [(ngModel)]="createForm.isActive" [binary]="true" name="isActive"></p-checkbox>
            </label>
            <label>
              <span>{{ t('insurance.products.fields.effectiveFrom') }}</span>
              <ain-date-input [(ngModel)]="createForm.effectiveFromUtc" name="effectiveFromUtc" />
            </label>
            <label>
              <span>{{ t('insurance.products.fields.effectiveTo') }}</span>
              <ain-date-input [(ngModel)]="createForm.effectiveToUtc" name="effectiveToUtc" />
            </label>
          </form>
        </section>

        @if (error()) { <p class="error">{{ error() }}</p> }
      } @else if (!product()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/products']">{{ t('catalog.action.backToList') }}</a>
          <button
            pButton
            type="button"
            [label]="t('common.save')"
            [disabled]="saving() || !canSaveEdit()"
            (click)="saveEdit()"></button>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.products.sections.info') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.products.help.info.title'"
              [descriptionKey]="'insurance.products.help.info.description'"
              [bulletKeys]="[
                'insurance.products.help.info.bullets.lob',
                'insurance.products.help.info.bullets.dates',
                'insurance.products.help.info.bullets.offering'
              ]" />
          </div>
          <div class="summary">
            <span>{{ t('insurance.products.fields.code') }}: <strong>{{ product()!.code }}</strong></span>
            <span>{{ t('columns.status') }}: <strong>{{ product()!.isActive ? t('common.yes') : t('common.no') }}</strong></span>
            <span>{{ t('insurance.products.fields.lineOfBusiness') }}: <strong>{{ lobLabel(product()!.lineOfBusiness) }}</strong></span>
          </div>
          <form class="grid" (ngSubmit)="saveEdit()">
            <label>
              <span>{{ t('insurance.products.fields.name') }}</span>
              <input pInputText [(ngModel)]="editForm.name" name="name" />
            </label>
            <label>
              <span>{{ t('insurance.products.fields.lineOfBusiness') }}</span>
              <p-select
                [options]="lineOfBusinessOptions()"
                optionValue="value"
                [(ngModel)]="editForm.lineOfBusiness"
                name="lineOfBusiness"
                [showClear]="true"
                [filter]="true"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? item.label : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ item.label }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.products.fields.offeringId') }}</span>
              <p-select
                [options]="offeringOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="editForm.offeringId"
                name="offeringId"
                [showClear]="true"
                [filter]="true"
                appendTo="body" />
            </label>
            <label>
              <span>{{ t('insurance.products.fields.policyDocumentTemplateId') }}</span>
              <p-select
                [options]="templateOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="editForm.policyDocumentTemplateId"
                name="editPolicyDocumentTemplateId"
                [showClear]="true"
                [filter]="true"
                appendTo="body" />
            </label>
            <label>
              <span>{{ t('insurance.products.fields.active') }}</span>
              <p-checkbox [(ngModel)]="editForm.isActive" [binary]="true" name="isActive"></p-checkbox>
            </label>
            <label>
              <span>{{ t('insurance.products.fields.effectiveFrom') }}</span>
              <ain-date-input [(ngModel)]="editForm.effectiveFromUtc" name="editEffectiveFromUtc" />
            </label>
            <label>
              <span>{{ t('insurance.products.fields.effectiveTo') }}</span>
              <ain-date-input [(ngModel)]="editForm.effectiveToUtc" name="editEffectiveToUtc" />
            </label>
          </form>
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'summary'" (click)="activeTab.set('summary')">{{ t('insurance.productDetail.tabs.summary') }}</button>
            <button type="button" [class.active]="activeTab() === 'versions'" (click)="activeTab.set('versions')">{{ t('insurance.productDetail.tabs.versions') }}</button>
          </div>

          @if (activeTab() === 'summary') {
            <div class="lines-header">
              <h4>{{ t('insurance.productDetail.tabs.summary') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.products.help.summary.title'"
                [descriptionKey]="'insurance.products.help.summary.description'"
                [bulletKeys]="[
                  'insurance.products.help.summary.bullets.readonly',
                  'insurance.products.help.summary.bullets.validity'
                ]" />
            </div>
            <div class="summary-grid">
              <p><strong>{{ t('insurance.products.fields.code') }}:</strong> {{ product()!.code }}</p>
              <p><strong>{{ t('insurance.products.fields.name') }}:</strong> {{ product()!.name }}</p>
              <p><strong>{{ t('insurance.products.fields.lineOfBusiness') }}:</strong> {{ lobLabel(product()!.lineOfBusiness) }}</p>
              <p><strong>{{ t('insurance.products.fields.active') }}:</strong> {{ product()!.isActive ? t('common.yes') : t('common.no') }}</p>
              <p><strong>{{ t('insurance.products.fields.effectiveFrom') }}:</strong> {{ product()!.effectiveFromUtc | ainDate }}</p>
              <p><strong>{{ t('insurance.products.fields.effectiveTo') }}:</strong> {{ product()!.effectiveToUtc ? (product()!.effectiveToUtc! | ainDate) : '-' }}</p>
              <p class="wide"><strong>{{ t('insurance.products.fields.offeringId') }}:</strong> {{ offeringLabel(product()!.offeringId) }}</p>
              <p class="wide"><strong>{{ t('insurance.products.fields.policyDocumentTemplateId') }}:</strong> {{ templateLabel(product()!.policyDocumentTemplateId ?? null) }}</p>
            </div>
          } @else {
            <div class="lines-header">
              <h4>{{ t('insurance.productDetail.tabs.versions') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.products.help.versions.title'"
                  [descriptionKey]="'insurance.products.help.versions.description'"
                  [bulletKeys]="[
                    'insurance.products.help.versions.bullets.lifecycle',
                    'insurance.products.help.versions.bullets.navigate'
                  ]" />
                <button pButton type="button" [label]="t('insurance.productVersions.actions.create')" (click)="openCreateVersion()"></button>
              </div>
            </div>
            @if (product()!.versions.length === 0) {
              <ain-state type="empty" [title]="t('insurance.productVersions.states.empty')" [message]="t('insurance.productVersions.states.emptyMessage')" />
            } @else {
              <p-table [value]="product()!.versions" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.productVersions.fields.versionNumber') }}</th>
                    <th>{{ t('insurance.productVersions.fields.status') }}</th>
                    <th>{{ t('insurance.productVersions.fields.minPremium') }}</th>
                    <th>{{ t('insurance.productVersions.fields.maxSumInsured') }}</th>
                    <th>{{ t('insurance.productVersions.fields.publishedAt') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>
                      <button type="button" class="link-btn" (click)="openVersion(row)">{{ versionLabel(row.versionNumber) }}</button>
                    </td>
                    <td>{{ row.status }}</td>
                    <td>{{ row.minPremium ?? '-' }}</td>
                    <td>{{ row.maxSumInsured ?? '-' }}</td>
                    <td>{{ row.publishedAtUtc ? (row.publishedAtUtc | ainDate) : '-' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }
        </section>

        @if (error()) { <p class="error">{{ error() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
    .section-title h3 { margin: 0; }
    .summary { display: flex; gap: 16px; flex-wrap: wrap; font-size: 14px; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #e5e7eb; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 10px; }
    .summary-grid .wide { grid-column: 1 / -1; }
    label { display: grid; gap: 6px; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 10px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .lines-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .lines-header h4 { margin: 0; }
    .lines-actions { display: flex; align-items: center; gap: 8px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) {
      .grid, .summary-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsuranceProductDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(InsuranceApiService);
  private readonly catalog = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly product = signal<InsuranceProductDetail | null>(null);
  readonly activeTab = signal<ProductTab>('summary');
  readonly isCreateMode = signal(false);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly policyDocumentTemplates = signal<InsurancePolicyDocumentTemplate[]>([]);
  private readonly baseLineOfBusinessOptions = [
    { value: 'motor', label: 'Motor' },
    { value: 'property', label: 'Property' },
    { value: 'life', label: 'Life' },
    { value: 'health', label: 'Health' },
    { value: 'liability', label: 'Liability' },
    { value: 'marine', label: 'Marine' },
    { value: 'credit', label: 'Credit' },
    { value: 'agriculture', label: 'Agriculture' },
    { value: 'travel', label: 'Travel' },
    { value: 'group', label: 'Group' },
    { value: 'reinsurance', label: 'Reinsurance' }
  ];

  createForm: CreateInsuranceProductPayload = {
    legalEntityId: '',
    code: '',
    name: '',
    lineOfBusiness: '',
    offeringId: null,
    policyDocumentTemplateId: null,
    isActive: true,
    effectiveFromUtc: new Date().toISOString(),
    effectiveToUtc: null
  };

  editForm: UpdateInsuranceProductPayload = {
    name: '',
    lineOfBusiness: '',
    offeringId: null,
    policyDocumentTemplateId: null,
    isActive: true,
    effectiveFromUtc: new Date().toISOString(),
    effectiveToUtc: null
  };

  ngOnInit(): void {
    this.catalog.listOfferings().subscribe({
      next: rows => this.offerings.set(rows),
      error: () => this.offerings.set([])
    });
    this.api.listPolicyDocumentTemplates().subscribe({
      next: rows => this.policyDocumentTemplates.set(rows),
      error: () => this.policyDocumentTemplates.set([])
    });

    const productId = this.route.snapshot.paramMap.get('productId');
    const isNewRoute = this.route.snapshot.url.at(-1)?.path === 'new';

    if (isNewRoute) {
      this.isCreateMode.set(true);
      this.loading.set(false);
      return;
    }

    if (!productId) {
      this.loading.set(false);
      return;
    }

    this.api.getProduct(productId).subscribe({
      next: item => {
        this.product.set(item);
        if (item) {
          this.editForm = {
            name: item.name,
            lineOfBusiness: item.lineOfBusiness,
            offeringId: item.offeringId,
            policyDocumentTemplateId: item.policyDocumentTemplateId ?? null,
            isActive: item.isActive,
            effectiveFromUtc: item.effectiveFromUtc,
            effectiveToUtc: item.effectiveToUtc
          };
        }
        this.loading.set(false);
      },
      error: () => {
        this.product.set(null);
        this.loading.set(false);
      }
    });
  }

  pageTitle(): string {
    return this.isCreateMode()
      ? this.t('insurance.products.form.createTitle')
      : this.t('insurance.products.form.editTitle');
  }

  saveCreate(): void {
    this.error.set('');
    this.saving.set(true);
    this.api.createProduct(this.createForm).subscribe({
      next: product => {
        this.saving.set(false);
        void this.router.navigate(['/insurance/products', product.id]);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  saveEdit(): void {
    const current = this.product();
    if (!current) {
      return;
    }

    this.error.set('');
    this.saving.set(true);
    this.api.updateProduct(current.id, this.editForm).subscribe({
      next: item => {
        this.product.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  openCreateVersion(): void {
    const current = this.product();
    if (!current) {
      return;
    }

    void this.router.navigate(['/insurance/products', current.id, 'versions', 'new']);
  }

  openVersion(version: InsuranceProductVersionListItem): void {
    const current = this.product();
    if (!current) {
      return;
    }

    void this.router.navigate(['/insurance/products', current.id, 'versions', version.id]);
  }

  versionLabel(versionNumber: number): string {
    return `${this.t('insurance.productVersions.item')} ${versionNumber}`;
  }

  offeringOptions(): Array<{ label: string; value: string }> {
    return this.offerings().map(item => ({
      value: item.id,
      label: `${item.code} - ${item.title}`
    }));
  }

  offeringLabel(offeringId: string | null): string {
    if (!offeringId) {
      return '-';
    }
    return this.offeringOptions().find(item => item.value === offeringId)?.label ?? offeringId;
  }

  templateOptions(): Array<{ label: string; value: string }> {
    return this.policyDocumentTemplates().map(item => ({
      value: item.id,
      label: item.isActive ? `${item.name} (${this.t('insurance.policyTemplate.status.active')})` : item.name
    }));
  }

  templateLabel(templateId: string | null): string {
    if (!templateId) {
      return '-';
    }
    return this.templateOptions().find(item => item.value === templateId)?.label ?? templateId;
  }

  lobLabel(value: string): string {
    return this.lineOfBusinessOptions().find(item => item.value === value)?.label ?? value;
  }

  lineOfBusinessOptions(): Array<{ label: string; value: string }> {
    const map = new Map<string, { value: string; label: string }>();
    const put = (value: string, label: string): void => {
      const trimmed = value.trim();
      if (!trimmed) {
        return;
      }

      const key = trimmed.toLowerCase();
      if (!map.has(key)) {
        map.set(key, { value: trimmed, label });
      }
    };

    for (const item of this.baseLineOfBusinessOptions) {
      put(item.value, item.label);
    }
    for (const offering of this.offerings()) {
      put(offering.type ?? '', offering.type ?? '');
    }

    const current = (this.editForm.lineOfBusiness || this.createForm.lineOfBusiness || '').trim();
    if (current) {
      const key = current.toLowerCase();
      const existing = map.get(key);
      map.set(key, { value: current, label: existing?.label ?? current });
    }

    return Array.from(map.values());
  }

  onCreateOfferingChanged(offeringId: string | null): void {
    if (!offeringId) {
      return;
    }

    const selected = this.offerings().find(item => item.id === offeringId);
    if (!selected?.type) {
      return;
    }

    if (!this.createForm.lineOfBusiness?.trim()) {
      this.createForm.lineOfBusiness = selected.type.toLowerCase();
    }
  }

  createCanSave(): boolean {
    return !!(
      this.createForm.legalEntityId.trim() &&
      this.createForm.code.trim() &&
      this.createForm.name.trim() &&
      this.createForm.lineOfBusiness.trim()
    );
  }

  canSaveEdit(): boolean {
    return !!(this.editForm.name.trim() && this.editForm.lineOfBusiness.trim());
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
